import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtINR, fmtDate } from "../lib/api";
import { Page, StatusPill } from "../components/Primitives";
import { ArrowLeft, FileText, ShieldCheck, ShieldPlus } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CarDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [car, setCar] = useState(null);
  const [active, setActive] = useState(0);
  const [warranties, setWarranties] = useState([]);

  useEffect(() => {
    api.get(`/showroom/cars/${id}`).then(({ data }) => setCar(data));
    api.get("/warranties", { params: { source_car_id: id } }).then(({ data }) => setWarranties(data));
  }, [id]);

  if (!car) return <Page number="02" title="Loading…" />;

  const imgs = car.image_ids || [];

  return (
    <Page
      number="02"
      title={`${car.make} ${car.model}`}
      subtitle={`${car.year} · ${car.variant || ""} · ${car.registration_no || "—"}`}
      right={
        <button onClick={() => nav(-1)} className="kk-btn-ghost" data-testid="back-btn">
          <ArrowLeft size={14} /> Back
        </button>
      }
    >
      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <div className="aspect-[16/10] bg-[var(--kk-surface-2)] border border-[var(--kk-border)] overflow-hidden">
            {imgs[active] ? (
              <img src={`${BACKEND}/api/files/${imgs[active]}`} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[var(--kk-muted)]">No photos yet</div>
            )}
          </div>
          {imgs.length > 1 && (
            <div className="grid grid-cols-6 gap-2 mt-3">
              {imgs.map((id, i) => (
                <button key={id} onClick={() => setActive(i)}
                        className={"aspect-square border " + (active === i ? "border-[var(--kk-black)]" : "border-[var(--kk-border)]")}>
                  <img src={`${BACKEND}/api/files/${id}`} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="font-display text-4xl">{fmtINR(car.price)}</div>
            <StatusPill status={car.status} />
          </div>
          <dl className="mt-6 divide-y divide-[var(--kk-border)] border-t border-b border-[var(--kk-border)]">
            {[
              ["KMs", car.kms?.toLocaleString?.()],
              ["Fuel", car.fuel],
              ["Transmission", car.transmission],
              ["Color", car.color],
              ["Owners", car.owners],
              ["VIN", car.vin],
              ["RC #", car.rc_no],
              ["Insurance", fmtDate(car.insurance_expiry)],
              ["Cost Price", fmtINR(car.cost_price)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2 text-sm">
                <dt className="kk-overline">{k}</dt>
                <dd className="font-mono">{v || "—"}</dd>
              </div>
            ))}
          </dl>
          {(car.features || []).length > 0 && (
            <div className="mt-5">
              <div className="kk-overline mb-2">Features</div>
              <div className="flex flex-wrap gap-2">
                {car.features.map((f) => <span key={f} className="kk-pill kk-pill-muted">{f}</span>)}
              </div>
            </div>
          )}
          {(car.doc_ids || []).length > 0 && (
            <div className="mt-5">
              <div className="kk-overline mb-2">Documents</div>
              <ul className="space-y-1">
                {car.doc_ids.map((d) => (
                  <li key={d}>
                    <a href={`${BACKEND}/api/files/${d}`} target="_blank" rel="noreferrer"
                       className="flex items-center gap-2 text-sm underline">
                      <FileText size={14} /> doc · {d.slice(0, 8)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {car.notes && (
            <div className="mt-5">
              <div className="kk-overline mb-2">Notes</div>
              <p className="text-sm">{car.notes}</p>
            </div>
          )}

          <div className="mt-6 border-t border-[var(--kk-border)] pt-5">
            <div className="flex items-center justify-between mb-3">
              <div className="kk-overline flex items-center gap-1"><ShieldCheck size={12}/> Warranty</div>
              <button
                onClick={() => nav(`/warranty?car=${car.id}&new=1`)}
                className="kk-pill kk-pill-black"
                data-testid="add-warranty-from-car"
              >
                <ShieldPlus size={12}/> Add
              </button>
            </div>
            {warranties.length === 0 ? (
              <div className="text-xs text-[var(--kk-muted)]">No warranty recorded for this car.</div>
            ) : (
              <ul className="space-y-2">
                {warranties.map((w) => (
                  <li key={w.id} onClick={() => nav("/warranty")}
                      className="flex items-center justify-between border-b border-[var(--kk-border)] pb-2 cursor-pointer">
                    <div>
                      <div className="text-sm font-semibold">{w.title}</div>
                      <div className="text-xs text-[var(--kk-muted)] font-mono">{w.code} · {fmtDate(w.start_date)} → {fmtDate(w.end_date)}</div>
                    </div>
                    <StatusPill status={w.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
}
